'use client';
import ArrowLeft from '@/app/_components/ui/icons/ArrowLeft.svg';
import React, { useState, useEffect, useCallback } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';

function Pagination({ itemsPerPage, totalItems }) {
  const [pageNumbers, setPageNumbers] = useState([]);
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const currentPage = parseInt(searchParams.get('page')) || 1;

  const createQueryString = useCallback(
    (name, value) => {
      const params = new URLSearchParams(searchParams.toString());
      if (value) {
        params.set(name, value);
      } else {
        params.delete(name);
      }
      return params.toString();
    },
    [searchParams]
  );

  const generatePaginationLink = (page) => {
    if (page === 1) {
      // Remove the 'page' query parameter if page is 1
      return createQueryString('page')
        ? pathname + '?' + createQueryString('page')
        : pathname;
    } else {
      // Update or add the 'page' query parameter based on the existing URL
      return pathname + '?' + createQueryString('page', page);
    }
  };

  useEffect(() => {
    const pages = [];

    // Generate the page numbers
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i);
    }

    setPageNumbers(pages);
  }, [totalItems, itemsPerPage, totalPages]);

  // Calculate the starting and ending indexes of the page numbers to display
  let startIndex = currentPage - 2;
  let endIndex = currentPage + 2;

  if (startIndex < 1) {
    startIndex = 1;
    endIndex = Math.min(totalPages, 5);
  }

  if (endIndex > totalPages) {
    endIndex = totalPages;
    startIndex = Math.max(1, totalPages - 4);
  }

  // Generate the page number buttons
  const pageButtons = pageNumbers
    .slice(startIndex - 1, endIndex)
    .map((page) => (
      <Link
        href={`${generatePaginationLink(page)}`}
        key={page}
        className={`mx-1 px-2 py-1 rounded-md ${
          currentPage == page
            ? 'border border-color-primary text-color-primary'
            : 'border border-gray-300 text-gray-300'
        }`}
      >
        {page}
      </Link>
    ));

  if (totalPages > 1) {
    return (
      <div className="flex justify-center paginationList pt-4">
        {currentPage != 1 && (
          <Link
            href={`${generatePaginationLink(currentPage - 1)}`}
            className="flex items-center"
          >
            <ArrowLeft fill="#454f5b" className="rotate-180" />
          </Link>
        )}

        {startIndex > 1 && (
          <>
            <Link
              href={`${generatePaginationLink(1)}`}
              className={`mx-1 px-2 py-1 rounded-md ${
                currentPage === totalPages
                  ? 'border border-color-primary text-color-primary'
                  : 'border border-gray-300 text-gray-300'
              }`}
            >
              {' '}
              1
            </Link>
            {startIndex > 2 && <span>...</span>}
          </>
        )}
        {pageButtons}
        {endIndex < totalPages && (
          <>
            {endIndex < totalPages - 1 && <span>...</span>}
            <Link
              href={`${generatePaginationLink(totalPages)}`}
              className={`mx-1 px-2 py-1 rounded-md ${
                currentPage === totalPages
                  ? 'border border-color-primary text-color-primary'
                  : 'border border-gray-300 text-gray-300'
              }`}
            >
              {totalPages}
            </Link>
          </>
        )}
        {/* arrow left button */}
        {currentPage != totalPages && (
          <Link
            href={`${generatePaginationLink(currentPage + 1)}`}
            className="flex items-center"
          >
            <ArrowLeft fill="#454f5b" />
          </Link>
        )}
      </div>
    );
  }

  return null;
}

export default Pagination;
